import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Sparkles,
  ArrowUpRight,
  Check,
  PenTool,
  Image as ImageIcon,
  Palette,
  Zap,
  BarChart3,
  Users,
  Calendar,
  Target,
  ChevronDown,
  ChevronUp,
  Share2,
} from 'lucide-react';
import { useState, useEffect } from 'react';

const features = [
  { icon: PenTool, title: 'AI-powered content generation', text: 'Generate blog posts, social media captions, email newsletters, and marketing copy with AI assistance tailored to your brand voice.' },
  { icon: ImageIcon, title: 'Visual content creation', text: 'Create stunning images, graphics, and social media visuals with built-in design tools and AI-powered image generation.' },
  { icon: Palette, title: 'Brand consistency tools', text: 'Maintain brand consistency with style guides, color palettes, font libraries, and approval workflows.' },
  { icon: BarChart3, title: 'Content performance analytics', text: 'Track engagement, reach, and conversion metrics across all your content in one unified dashboard.' },
];

const useCases = [
  { title: 'Social media campaigns', text: 'Create and schedule posts for Facebook, LinkedIn, Instagram, X, YouTube, and TikTok from one platform.' },
  { title: 'Blog & content marketing', text: 'Generate SEO-optimized blog posts and articles with AI assistance and brand-consistent styling.' },
  { title: 'Email marketing', text: 'Design and send personalized email campaigns with automated workflows and A/B testing.' },
  { title: 'Customer education', text: 'Create tutorials, guides, and help center content to educate and support your customers.' },
];

const benefits = [
  { icon: Zap, title: '10x faster content creation', description: 'AI-assisted writing and design help your team produce high-quality content in minutes instead of hours.' },
  { icon: Users, title: 'Team collaboration', description: 'Work together in real-time with commenting, approval workflows, and role-based permissions.' },
  { icon: Calendar, title: 'Content calendar', description: 'Plan, schedule, and publish content across all channels with a unified content calendar.' },
  { icon: Target, title: 'Audience targeting', description: 'Segment your audience and personalize content for different customer segments and regions.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is Content Lab?',
    answer: 'Content Lab is an Enterprise content creation and management platform that helps teams generate, collaborate on, and publish content across multiple channels with AI assistance.',
  },
  {
    question: 'How does AI-powered content generation work?',
    answer: 'Our AI learns your brand voice, style guidelines, and content patterns to generate drafts that match your brand. You can edit, approve, or regenerate as needed.',
  },
  {
    question: 'Can multiple team members collaborate?',
    answer: 'Yes. Content Lab includes real-time collaboration with commenting, approval workflows, and role-based permissions for teams of any size.',
  },
  {
    question: 'Is Content Lab included in Enterprise?',
    answer: 'Yes. Content Lab is an exclusive feature included in the Enterprise plan at no additional cost.',
  },
  {
    question: 'Can I schedule content in advance?',
    answer: 'Yes. Content Lab includes a unified content calendar where you can plan, schedule, and auto-publish content across supported platforms.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ContentLab() {
  useEffect(() => {
    // Inject ZuriChat widget
    if (typeof window !== 'undefined' && !document.getElementById('zuri-chat-widget')) {
      const configScript = document.createElement('script');
      configScript.id = 'zuri-chat-widget-config';
      configScript.innerHTML = `
        window.zuriChatConfig = {
          workspaceId: 'ws_mr9ll0xx_1ctlv5zs',
          position: 'bottom-right',
          color: '#041dd7',
          headerColor: '#c90d0d',
          enableAI: true,
          aiModel: 'gpt-4o-mini',
        };
      `;
      document.head.appendChild(configScript);

      const widgetScript = document.createElement('script');
      widgetScript.id = 'zuri-chat-widget';
      widgetScript.src = 'https://media.getzurihub.com/widget.js';
      widgetScript.async = true;
      document.body.appendChild(widgetScript);
    }
  }, []);
  return (
    <>
      <SEO
        title="Content Lab - Enterprise Content Creation Platform | ZuriHub"
        description="Create, collaborate, and publish content across Facebook, LinkedIn, Instagram, X, YouTube, and TikTok with AI-powered Content Lab for Enterprise. Real-time collaboration and brand consistency tools."
        path="/content-lab"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Sparkles className="h-4 w-4" /> Enterprise Only
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Enterprise content that <span className="gradient-text">scales with your brand</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Create, collaborate, and publish content across Facebook, LinkedIn, Instagram, X, YouTube, and TikTok with AI-powered tools and real-time team collaboration.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Sparkles className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Clean_Hero.png"
                  alt="Content Lab dashboard showing AI content generation and collaboration features"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful content features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need for enterprise content creation.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why Enterprise needs Content Lab</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform content creation.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                From ideation to publication, Content Lab streamlines every step of content creation with AI assistance and team collaboration.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: PenTool,
                    title: 'Generate content',
                    description: 'AI generates content drafts based on your brief, brand voice, and target audience.',
                  },
                  {
                    icon: Users,
                    title: 'Collaborate',
                    description: 'Team reviews, comments, and approves with real-time editing and version control.',
                  },
                  {
                    icon: Share2,
                    title: 'Publish',
                    description: 'Schedule or publish instantly across all connected social media platforms.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Content Lab features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> AI-powered content generation</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Real-time team collaboration</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Brand consistency tools</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Multi-platform publishing</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Content calendar and scheduling</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Performance analytics and reporting</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about Content Lab</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
